import type { DemoRole } from '../roles';
import type { DemoAction } from './actions';
import { act1Reducer, initialAct1, type IAct1State } from './act1';
import { act2Reducer, initialAct2, type IAct2State } from './act2';
import { act4Reducer, initialAct4, type IAct4State } from './act4';
import {
  initialInitialReview,
  initialReviewReducer,
  type IInitialReviewState
} from './initial-review';

/**
 * The demo's whole state: one slice per act, the initial review's own, and the role being viewed
 * as.
 *
 * **One reducer per slice, and every one sees every action** — so an action one screen dispatches
 * changes whichever slice owns what it touches (an initial-review decision is act 2's record)
 * without any act writing into another's. "Reset demo" is `initialDemoState()`, and nothing else
 * holds the state: it is in memory, it survives moving between screens, and a reload starts over.
 */
export interface IDemoState {
  viewAs: Nullable<DemoRole>;
  act1: IAct1State;
  act2: IAct2State;
  act4: IAct4State;
  /** The initial review's survey and thresholds — a screen's state, not an act's. */
  initialReview: IInitialReviewState;
}

export function initialDemoState(): IDemoState {
  return {
    viewAs: null,
    act1: initialAct1(),
    act2: initialAct2(),
    act4: initialAct4(),
    initialReview: initialInitialReview()
  };
}

export function demoReducer(state: IDemoState, action: DemoAction): IDemoState {
  if (action.type === 'reset') return initialDemoState();
  if (action.type === 'view-as') return { ...state, viewAs: action.role };
  return {
    viewAs: state.viewAs,
    act1: act1Reducer(state.act1, action),
    act2: act2Reducer(state.act2, action),
    act4: act4Reducer(state.act4, action),
    initialReview: initialReviewReducer(state.initialReview, action)
  };
}
